import React, { useCallback, useContext, useState } from 'react';
import TabIntro from '../../Common/Form/Tab/TabIntro';
import LastUpdated from '../LastUpdated';
import { UserContext } from '../UserContext';
import Select from 'react-select';
import { object } from 'yup';
import {
    Button,
    ErrorMessage,
    InputGroup,
    Label,
    TextField,
    clean,
    locationData,
    validationRules,
    extractErrors,
} from '../../Common/Form';
import { TabContext } from '../../Common/Form/Tab/TabContext';
import Feedback from '../Feedback';
import useMainContent from '../../../hooks/useMainContent';

const validationSchema = object({
    'fields[memberAddressLine1]': validationRules.address1,
    'fields[memberAddressLine2]': validationRules.address2,
    'fields[memberCity]': validationRules.city,
    'fields[memberCountry]': validationRules.country,
    'fields[memberDepartment]': validationRules.department,
    'fields[memberNickname]': validationRules.nickname,
    'fields[memberPhoneNumber]': validationRules.phone,
    'fields[memberState]': validationRules.state,
    'fields[memberTimeZone]': validationRules.timezone,
    'fields[memberTitle]': validationRules.memberTitle,
    'fields[memberZip]': validationRules.zip,
    'fields[peopleLinkedin]': validationRules.linkedin,
    'fields[personTwitter]': validationRules.twitter,
    firstName: validationRules.firstName,
    lastName: validationRules.lastName,
});

const PersonalInformationForm = () => {
    const { user } = useContext(UserContext);
    const { tabTouched, setTabTouched, mustSave } = useContext(TabContext);

    const [values, setValues] = useState({});
    const [errors, setErrors] = useState({});
    const [globalError, setGlobalError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [states, setStates] = useState(locationData.US);
    const [userPhoto, setUserPhoto] = useState(null);

    const scrollToMainContent = useMainContent();

    if (user !== null && Object.keys(values).length === 0) {
        const selectedCountry = locationData.countries.find((c) => c.value === user.country);
        setStates(locationData[selectedCountry.value] ?? []);
        const selectedState = (locationData[selectedCountry.value] ?? []).find((s) => s.value === user.state);

        setValues({
            ...user,
            country: selectedCountry,
            state: selectedState,
            timezone: locationData.timezones.find((t) => t.value === user.timezone),
            twitter: (user.twitter || '').replace(new RegExp('https?://(www.)?(twitter|x).com/', 'i'), ''),
            linkedin: (user.linkedin || '').replace(new RegExp('https?://(www.)?linkedin.com/(in/)?', 'i'), ''),
        });

        setUserPhoto(user.userPhoto);
    }

    const onSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setIsSubmitting(true);

            const formData = new FormData(e.currentTarget);

            formData.set('firstName', clean(formData.get('firstName')));
            formData.set('lastName', clean(formData.get('lastName')));
            formData.set('fields[memberNickname]', clean(formData.get('fields[memberNickname]')));
            formData.set('fields[memberTitle]', clean(formData.get('fields[memberTitle]')));
            formData.set('fields[memberDepartment]', clean(formData.get('fields[memberDepartment]')));
            formData.set('fields[memberCity]', clean(formData.get('fields[memberCity]')));
            formData.set('fields[memberAddressLine1]', clean(formData.get('fields[memberAddressLine1]')));
            formData.set('fields[memberAddressLine2]', clean(formData.get('fields[memberAddressLine2]')));

            const data = Object.fromEntries(formData.entries());

            await validationSchema
                .validate(data, { strict: true, abortEarly: false })
                .then(() => {
                    setErrors({});
                    setGlobalError(false);

                    formData.append('userId', user.userId);
                    formData.append('action', 'users/save-user');

                    if (typeof userPhoto !== 'object') {
                        formData.delete('photo');
                    }

                    fetch('/', {
                        method: 'POST',
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'X-CSRF-Token': window.token,
                        },
                        body: formData,
                    })
                        .then(() => {
                            setTabTouched(false);
                            setSuccess(true);
                            setIsSubmitting(false);
                        })
                        .catch((err) => {
                            setGlobalError(true);
                        });
                })
                .catch((errs) => {
                    setErrors(extractErrors(errs));
                    setIsSubmitting(false);
                })
                .finally(() => {
                    scrollToMainContent();
                });
        },
        [user, userPhoto, setTabTouched, setGlobalError, setErrors, setIsSubmitting, scrollToMainContent]
    );

    const changeCountry = useCallback(
        (c) => {
            const stateOptions = c && Object.hasOwn(locationData, c.value) ? locationData[c.value] : [];
            setStates(stateOptions);
        },
        [setStates]
    );

    if (Object.entries(values).length === 0) {
        return null;
    }

    return (
        <>
            <TabIntro text="Thank you for keeping your personal information up to date, as this data is used throughout the community for registration and communications.">
                <LastUpdated date={values.dateUpdated} />
            </TabIntro>

            <Feedback
                success={success}
                globalError={globalError}
                mustSave={mustSave}
                errors={errors}
                setSuccess={setSuccess}
            />

            <form className="form" onSubmit={onSubmit} onInput={() => !tabTouched && setTabTouched(true)}>
                <fieldset>
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="note">
                        <span className="required">*</span> Indicates required field
                    </label>
                    <div className="grid -col3 -form">
                        <div className="grid__cell">
                            <TextField id="firstName" label="First name" required value={values.firstName} />
                            <ErrorMessage message={errors.firstName ?? ''} />
                        </div>
                        <div className="grid__cell">
                            <TextField id="lastName" label="Last name" required value={values.lastName} />
                            <ErrorMessage message={errors.lastName ?? ''} />
                        </div>
                        <div className="grid__cell">
                            <TextField
                                id="fields[memberNickname]"
                                label="Nickname"
                                required={false}
                                value={values.nickname}
                            />
                            <ErrorMessage message={errors['fields[memberNickname]'] ?? ''} />
                        </div>
                    </div>
                </fieldset>

                <fieldset>
                    <div className="grid -col3 -form">
                        <div className="grid__cell">
                            <TextField id="account" label="Account Name / Firm" required value={values.acct} disabled />
                        </div>
                        <div className="grid__cell">
                            <TextField
                                id="fields[memberDepartment]"
                                label="Department"
                                required={false}
                                value={values.department}
                            />
                            <ErrorMessage message={errors['fields[memberDepartment]'] ?? ''} />
                        </div>
                        <div className="grid__cell">
                            <TextField
                                id="fields[memberTitle]"
                                label="Job Title / Position"
                                required
                                value={values.jobTitle}
                            />
                            <ErrorMessage message={errors['fields[memberTitle]'] ?? ''} />
                        </div>
                    </div>
                </fieldset>

                <fieldset>
                    <div className="grid -col3 -form">
                        <div className="grid__cell">
                            <TextField
                                id="fields[memberPhoneNumber]"
                                label="Phone Number"
                                required
                                value={values.phone}
                            />
                            <ErrorMessage message={errors['fields[memberPhoneNumber]'] ?? ''} />
                        </div>
                    </div>
                </fieldset>

                <fieldset>
                    <div>
                        <div className="grid -form">
                            <div className="grid__cell">
                                <TextField
                                    id="fields[memberAddressLine1]"
                                    label="Office Address"
                                    required
                                    value={values.address1}
                                />
                                <TextField
                                    id="fields[memberAddressLine2]"
                                    label="Address Line 2"
                                    hideLabel
                                    required
                                    value={values.address2}
                                />
                                <ErrorMessage message={errors['fields[memberAddressLine1]'] ?? ''} />
                                <ErrorMessage message={errors['fields[memberAddressLine2]'] ?? ''} />
                            </div>
                        </div>
                    </div>
                </fieldset>

                <fieldset>
                    <div className="grid -col3 -form">
                        <div className="grid__cell">
                            <TextField id="fields[memberCity]" label="City" required value={values.city} />
                            <ErrorMessage message={errors['fields[memberCity]'] ?? ''} />
                        </div>
                        <div className="grid__cell">
                            <Label id="fields[memberState]" text="State / Province / Region" required={false} />
                            <Select
                                className="v-select select__options vs--single vs--searchable"
                                classNamePrefix="vs"
                                isClearable
                                name="fields[memberState]"
                                placeholder=""
                                defaultValue={values.state}
                                options={states}
                            />
                            <ErrorMessage message={errors['fields[memberState]'] ?? ''} />
                        </div>
                        <div className="grid__cell">
                            <TextField id="fields[memberZip]" label="Postal Code" required value={values.zip} />
                            <ErrorMessage message={errors['fields[memberZip]'] ?? ''} />
                        </div>
                    </div>
                </fieldset>

                <fieldset>
                    <div className="grid -col2 -form">
                        <div className="grid__cell">
                            <Label id="fields[memberCountry]" text="Country" required />
                            <Select
                                className="v-select select__options vs--single vs--searchable"
                                classNamePrefix="vs"
                                isClearable
                                name="fields[memberCountry]"
                                placeholder=""
                                defaultValue={values.country}
                                options={locationData.countries}
                                onChange={changeCountry}
                            />
                            <ErrorMessage message={errors['fields[memberCountry]'] ?? ''} />
                        </div>
                        <div className="grid__cell">
                            <Label id="fields[memberTimeZone]" text="Time Zone" required />
                            <Select
                                className="v-select select__options vs--single vs--searchable"
                                classNamePrefix="vs"
                                isClearable
                                name="fields[memberTimeZone]"
                                placeholder=""
                                defaultValue={values.timezone}
                                options={locationData.timezones}
                            />
                            <ErrorMessage message={errors['fields[memberTimeZone]'] ?? ''} />
                        </div>
                    </div>
                </fieldset>

                <fieldset>
                    <div className="grid -form">
                        <div className="grid__cell text">
                            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                            <label>Social Media Links</label>
                            <InputGroup
                                symbol="twitter"
                                text="https://twitter.com/"
                                id="fields[personTwitter]"
                                label="Twitter"
                                value={values.twitter}
                                required={false}
                            />
                            <ErrorMessage message={errors['fields[personTwitter]'] ?? ''} />
                            <InputGroup
                                symbol="linkedin"
                                text="https://linkedin.com/in/"
                                id="fields[peopleLinkedin]"
                                label="LinkedIn"
                                value={values.linkedin}
                                required={false}
                            />
                            <ErrorMessage message={errors['fields[peopleLinkedin]'] ?? ''} />
                        </div>
                    </div>
                </fieldset>

                <fieldset>
                    <div className="grid -form">
                        <div className="grid__cell">
                            <label htmlFor="file">Profile Image</label>
                            <div id="user-photo" className="custom-file-container fileUpload">
                                {!!userPhoto && (
                                    <div
                                        className="custom-file-container__image-preview fileUpload__preview"
                                        style={{
                                            backgroundImage: `url(${
                                                typeof userPhoto === 'object'
                                                    ? URL.createObjectURL(userPhoto)
                                                    : userPhoto
                                            }`,
                                        }}
                                    />
                                )}
                                <label className="custom-file-container__custom-file inputGroup">
                                    <input
                                        name="photo"
                                        type="file"
                                        accept="image/png,image/jpeg"
                                        aria-label="Choose File"
                                        className="custom-file-container__custom-file__custom-file-input inputGroup__input"
                                        onChange={(e) => setUserPhoto(e.target.files[0])}
                                    />
                                    <span className="custom-file-container__custom-file__custom-file-control">
                                        {typeof userPhoto === 'object'
                                            ? userPhoto.name
                                            : userPhoto.substring(userPhoto.lastIndexOf('/') + 1)}
                                        <span className="custom-file-container__custom-file__custom-file-control__button">
                                            Change Image
                                        </span>
                                    </span>
                                </label>
                            </div>
                        </div>
                        <div className="grid__cell" />
                    </div>
                </fieldset>

                <div className="form__actions">
                    <Button text="Save Information" className="-cta" disabled={isSubmitting} />
                </div>
            </form>
        </>
    );
};

PersonalInformationForm.propTypes = {};

export default PersonalInformationForm;
